import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import Layout from '../../components/Layout';
import {
  Upload,
  X,
  CreditCard,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Loader2,
  FileText,
  FileSpreadsheet,
  Building2,
  User,
  ArrowLeft,
  Download,
  Eye,
  EyeOff,
  Copy,
  ChevronDown,
  ChevronUp,
  Fingerprint,
  Trash2,
  Users,
  Sparkles,
  Plug,
  Landmark,
  Mail,
  ChevronRight,
  BookOpen,
  Calendar,
  HardDrive,
} from 'lucide-react';
import api, { clientsAPI, suppliersAPI, accountingAPI, settingsAPI, authAPI, subscriptionsAPI } from '../../api';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { MinimalRichTextEditor } from '../../components/ui/MinimalRichTextEditor';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import ImportColumnMapper from '../../components/ImportColumnMapper';
import ImportPreview from '../../components/ImportPreview';
import InvoiceStyleEditor from '../../components/invoices/InvoiceStyleEditor';
import RoutineEmailsSettings from '../../components/settings/RoutineEmailsSettings';
import BackupExportSettings from '../../components/settings/BackupExportSettings';
import HmrcSettings from '../../components/settings/HmrcSettings';
import { useToast } from '../../hooks/useToast';

function isValidSettingsTab(tab, supportsPasskey) {
  if (!tab || typeof tab !== 'string') return false;
  if (tab === 'security') return supportsPasskey;
  return [
    'company',
    'subscription',
    'invoice',
    'import',
    'accounting',
    'ai',
    'payments',
    'integrations',
    'hmrc',
    'routine-emails',
    'backup-export',
  ].includes(tab);
}

function hasLinkedStripeSubscription(sub) {
  const id = sub?.stripeSubscriptionId;
  return Boolean(id && String(id).startsWith('sub_'));
}

const SETTINGS_DETAIL_LABELS = {
  company: 'Company settings',
  subscription: 'Manage subscription',
  invoice: 'Invoice settings',
  'routine-emails': 'Routine emails',
  import: 'Import data',
  accounting: 'Accounting',
  ai: 'AI integrations',
  payments: 'Payments',
  integrations: 'Calendar & integrations',
  hmrc: 'HMRC Making Tax Digital',
  security: 'Security',
  'backup-export': 'Backup / Export Data',
};

/** Keep in sync with server/utils/clientDocumentEmailTemplate.js */
const CLIENT_EMAIL_PLACEHOLDERS = [
  { token: '{{clientName}}', description: 'Client display name' },
  { token: '{{tenantName}}', description: 'Your business name' },
  { token: '{{documentNumber}}', description: 'Invoice or estimate number' },
  { token: '{{publicLink}}', description: 'Secure link to view the document' },
  { token: '{{linkDays}}', description: 'Days the link stays valid' },
  { token: '{{documentLabel}}', description: 'Invoice or Estimate' },
  { token: '{{documentHeading}}', description: 'Short heading (e.g. Invoice notification)' },
  { token: '{{ctaLabel}}', description: 'Button label (e.g. View invoice)' },
  { token: '{{year}}', description: 'Current year' },
];

export default function TenantSettings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, isTenantAdmin } = useAuthStore();
  const [activeTab, setActiveTab] = useState('company');
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [billingCheckoutLoading, setBillingCheckoutLoading] = useState(false);
  const [subscriptionBillingCycle, setSubscriptionBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [hmrcRefreshKey, setHmrcRefreshKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoBlobUrlRef = useRef(null);
  const [accreditingLogoFile, setAccreditingLogoFile] = useState(null);
  const [accreditingLogoPreview, setAccreditingLogoPreview] = useState(null);
  const accreditingLogoBlobUrlRef = useRef(null);
  const [importType, setImportType] = useState('clients');
  const [importFile, setImportFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // upload, mapping, preview, results
  const [importMappings, setImportMappings] = useState(null);
  const [importPreviewData, setImportPreviewData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [seedingLoading, setSeedingLoading] = useState(false);
  const [aiSettings, setAiSettings] = useState({
    provider: 'openai',
    model: 'gpt-5.2',
    hasKey: false,
  });
  const [aiSettingsLoading, setAiSettingsLoading] = useState(false);
  const [aiSettingsSaving, setAiSettingsSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [usageStats, setUsageStats] = useState(null);
  const [usageStatsLoading, setUsageStatsLoading] = useState(false);
  const [pricingStatus, setPricingStatus] = useState(null);
  const [pricingStatusLoading, setPricingStatusLoading] = useState(false);
  const [paymentsData, setPaymentsData] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsSaving, setPaymentsSaving] = useState(false);
  const [paymentsShowSetup, setPaymentsShowSetup] = useState(false);
  const [stripeSecretInput, setStripeSecretInput] = useState('');
  const [stripeWebhookInput, setStripeWebhookInput] = useState('');
  const [stripeSecretDirty, setStripeSecretDirty] = useState(false);
  const [stripeWebhookDirty, setStripeWebhookDirty] = useState(false);
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [showStripeWebhook, setShowStripeWebhook] = useState(false);
  const [sumupApiKeyInput, setSumupApiKeyInput] = useState('');
  const [sumupWebhookInput, setSumupWebhookInput] = useState('');
  const [sumupApiKeyDirty, setSumupApiKeyDirty] = useState(false);
  const [sumupWebhookDirty, setSumupWebhookDirty] = useState(false);
  const [showSumupApiKey, setShowSumupApiKey] = useState(false);
  const [showSumupWebhook, setShowSumupWebhook] = useState(false);
  const [googleCalendarData, setGoogleCalendarData] = useState(null);
  const [googleCalendarLoading, setGoogleCalendarLoading] = useState(false);
  const [googleCalendarSaving, setGoogleCalendarSaving] = useState(false);
  const [googleClientIdInput, setGoogleClientIdInput] = useState('');
  const [googleClientSecretInput, setGoogleClientSecretInput] = useState('');
  const [googleClientIdDirty, setGoogleClientIdDirty] = useState(false);
  const [googleClientSecretDirty, setGoogleClientSecretDirty] = useState(false);
  const [showGoogleClientId, setShowGoogleClientId] = useState(false);
  const [showGoogleClientSecret, setShowGoogleClientSecret] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    company_reg_no: '',
    company_vat_no: '',
    logo_url: '',
    primary_color: '#3B82F6',
    secondary_color: '#10B981',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    phone: '',
    email: '',
    accrediting_body: '',
    accrediting_body_other: '',
    accrediting_body_logo_url: '',
    enrolment_no: '',
    branch: '',
    feature_flags: {},
    preferences: {},
  });
  const [invoiceSettings, setInvoiceSettings] = useState({
    default_payment_terms: '',
    default_vat_rate: 20,
    default_payment_due_days: 30,
    default_invoice_notes: '',
    default_invoice_terms: '',
    invoice_number_prefix: '',
    starting_invoice_number: 1,
    invoice_style: {},
    client_email_subject: '',
    client_email_text: '',
    client_email_html: '',
  });
  const [invoiceSettingsSubTab, setInvoiceSettingsSubTab] = useState('general');
  const [hubSection, setHubSection] = useState('general');
  const [passkeyCredentials, setPasskeyCredentials] = useState([]);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyAdding, setPasskeyAdding] = useState(false);
  const [passkeyDeletingId, setPasskeyDeletingId] = useState(null);
  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();
  const supportsPasskey = typeof window !== 'undefined' && browserSupportsWebAuthn();

  const tabParam = searchParams.get('tab');
  const detailTabValid =
    tabParam && isValidSettingsTab(tabParam, supportsPasskey);
  const isSettingsHub = !detailTabValid;

  useEffect(() => {
    if (!isTenantAdmin && hubSection === 'team') {
      setHubSection('general');
    }
  }, [isTenantAdmin, hubSection]);

  // Revoke blob URL we created for uploaded logo (auth-protected /api/settings/logo)
  const revokeLogoBlobUrl = () => {
    if (logoBlobUrlRef.current) {
      URL.revokeObjectURL(logoBlobUrlRef.current);
      logoBlobUrlRef.current = null;
    }
  };

  const revokeAccreditingLogoBlobUrl = () => {
    if (accreditingLogoBlobUrlRef.current) {
      URL.revokeObjectURL(accreditingLogoBlobUrlRef.current);
      accreditingLogoBlobUrlRef.current = null;
    }
  };

  const resolveLogoPreview = async (logoUrl) => {
    revokeLogoBlobUrl();
    if (!logoUrl) return null;
    if (logoUrl === '/api/settings/logo') {
      const res = await fetch('/api/settings/logo', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      logoBlobUrlRef.current = url;
      return url;
    }
    return logoUrl; // external URL or data URL
  };

  const resolveAccreditingLogoPreview = async (logoUrl) => {
    revokeAccreditingLogoBlobUrl();
    if (!logoUrl) return null;
    if (logoUrl === '/api/settings/accrediting-logo') {
      const res = await fetch('/api/settings/accrediting-logo', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      accreditingLogoBlobUrlRef.current = url;
      return url;
    }
    return logoUrl;
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/settings', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load settings');
        const data = await res.json();
        if (!cancelled) {
          const loadedSettings = { ...(data.settings || {}) };
          setSettings(loadedSettings);
          if (loadedSettings.logo_url) {
            const preview = await resolveLogoPreview(loadedSettings.logo_url);
            if (!cancelled && preview) setLogoPreview(preview);
          }
          if (loadedSettings.accrediting_body_logo_url) {
            const preview = await resolveAccreditingLogoPreview(loadedSettings.accrediting_body_logo_url);
            if (!cancelled && preview) setAccreditingLogoPreview(preview);
          }
          if (loadedSettings.preferences && loadedSettings.preferences.invoice) {
            const inv = loadedSettings.preferences.invoice;
            setInvoiceSettings({
              default_payment_terms: inv.default_payment_terms || '',
              default_vat_rate: inv.default_vat_rate ?? 20,
              default_payment_due_days: inv.default_payment_due_days || 30,
              default_invoice_notes: inv.default_invoice_notes || '',
              default_invoice_terms: inv.default_invoice_terms || '',
              invoice_number_prefix: inv.invoice_number_prefix || '',
              starting_invoice_number: inv.starting_invoice_number || 1,
              invoice_style: inv.invoice_style || {},
              client_email_subject: inv.client_email_subject || '',
              client_email_text: inv.client_email_text || '',
              client_email_html: inv.client_email_html || '',
            });
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
      revokeLogoBlobUrl();
      revokeAccreditingLogoBlobUrl();
    };
  }, [token]);

  // Load AI settings
  useEffect(() => {
    let cancelled = false;
    async function loadAISettings() {
      try {
        setAiSettingsLoading(true);
        const data = await settingsAPI.getAI();
        if (!cancelled) {
          setAiSettings(data);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load AI settings:', e);
        }
      } finally {
        if (!cancelled) {
          setAiSettingsLoading(false);
        }
      }
    }
    loadAISettings();
    return () => { cancelled = true; };
  }, []);

  // Load usage statistics and pricing status when AI tab is active
  useEffect(() => {
    if (activeTab !== 'ai') return;
    
    let cancelled = false;
    async function loadUsageData() {
      try {
        setUsageStatsLoading(true);
        setPricingStatusLoading(true);
        
        const [statsData, pricingData] = await Promise.all([
          settingsAPI.getAIUsage().catch(e => {
            console.error('Failed to load usage statistics:', e);
            return null;
          }),
          settingsAPI.getAIPricingStatus().catch(e => {
            console.error('Failed to load pricing status:', e);
            return null;
          }),
        ]);
        
        if (!cancelled) {
          setUsageStats(statsData);
          setPricingStatus(pricingData);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load usage data:', e);
        }
      } finally {
        if (!cancelled) {
          setUsageStatsLoading(false);
          setPricingStatusLoading(false);
        }
      }
    }
    loadUsageData();
    return () => { cancelled = true; };
  }, [activeTab]);

  // Load payments config when Payments tab is active
  useEffect(() => {
    if (activeTab !== 'payments') return;

    let cancelled = false;
    async function loadPayments() {
      try {
        setPaymentsLoading(true);
        const data = await settingsAPI.getPayments();
        if (!cancelled) {
          setPaymentsData(data);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load payments settings:', e);
          setPaymentsData(null);
        }
      } finally {
        if (!cancelled) {
          setPaymentsLoading(false);
        }
      }
    }
    loadPayments();
    return () => { cancelled = true; };
  }, [activeTab]);

  // Load Google Calendar integration config when Integrations tab is active
  useEffect(() => {
    if (activeTab !== 'integrations') return;

    let cancelled = false;
    async function loadGoogleCalendar() {
      try {
        setGoogleCalendarLoading(true);
        const data = await settingsAPI.getGoogleCalendarIntegration();
        if (!cancelled) {
          setGoogleCalendarData(data);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load Google Calendar integration:', e);
          setGoogleCalendarData(null);
        }
      } finally {
        if (!cancelled) {
          setGoogleCalendarLoading(false);
        }
      }
    }
    loadGoogleCalendar();
    return () => { cancelled = true; };
  }, [activeTab]);

  // Load passkey credentials when Security tab is active
  useEffect(() => {
    if (activeTab !== 'security' || !supportsPasskey) return;

    let cancelled = false;
    async function loadPasskeys() {
      try {
        setPasskeyLoading(true);
        const creds = await authAPI.webauthnCredentials();
        if (!cancelled) {
          setPasskeyCredentials(Array.isArray(creds) ? creds : creds.credentials || []);
        }
      } catch (e) {
        if (!cancelled) {
          setPasskeyCredentials([]);
        }
      } finally {
        if (!cancelled) {
          setPasskeyLoading(false);
        }
      }
    }
    loadPasskeys();
    return () => { cancelled = true; };
  }, [activeTab, supportsPasskey]);

  const handleAddPasskey = async () => {
    setPasskeyAdding(true);
    setError(null);
    try {
      const options = await authAPI.webauthnRegisterOptions();
      const response = await startRegistration({ optionsJSON: options });
      await authAPI.webauthnRegisterVerify({
        response,
        challenge: options.challenge,
      });
      toastSuccess('Passkey added', 'You can now sign in with your passkey.');
      const creds = await authAPI.webauthnCredentials();
      setPasskeyCredentials(Array.isArray(creds) ? creds : creds.credentials || []);
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.message?.includes('not allowed')) {
        toastError('Registration cancelled', 'Passkey registration was cancelled or timed out.');
      } else {
        toastError('Registration failed', err.response?.data?.error || err.message || 'Could not add passkey.');
      }
    } finally {
      setPasskeyAdding(false);
    }
  };

  const handleDeletePasskey = async (id) => {
    setPasskeyDeletingId(id);
    setError(null);
    try {
      await authAPI.webauthnCredentialDelete(id);
      toastSuccess('Passkey removed', 'The passkey has been deleted.');
      setPasskeyCredentials((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      toastError('Delete failed', err.response?.data?.error || err.message || 'Could not remove passkey.');
    } finally {
      setPasskeyDeletingId(null);
    }
  };

  const fetchSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError('');
      const response = await api.get('/subscriptions/current');
      setSubscription(response.data.subscription);
    } catch (err) {
      setSubscriptionError(err.response?.data?.error || 'Failed to load subscription');
      console.error('Error fetching subscription:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const goToSettingsHub = () => {
    navigate('/tenant/settings', { replace: true });
  };

  useEffect(() => {
    const t = searchParams.get('tab');
    if (!isValidSettingsTab(t, supportsPasskey)) {
      return;
    }
    setActiveTab(t);
    if (t === 'subscription' && !subscription) {
      fetchSubscription();
    }
  }, [searchParams, supportsPasskey, subscription]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const billing = searchParams.get('billing');
    if (tab !== 'subscription' || !billing) return;
    if (billing === 'success') {
      toastSuccess('Billing updated', 'Your subscription details will sync shortly.');
      fetchSubscription();
    } else if (billing === 'cancel') {
      toastError('Checkout cancelled', 'You can add a payment method when you are ready.');
    }
    navigate('/tenant/settings?tab=subscription', { replace: true });
  }, [searchParams, navigate, toastSuccess, toastError]);

  useEffect(() => {
    if (searchParams.get('tab') !== 'hmrc') return;
    const connected = searchParams.get('hmrc_connected');
    const hmrcError = searchParams.get('hmrc_error');
    if (connected) {
      toastSuccess('HMRC connected', `Successfully connected to HMRC ${connected}.`);
      setHmrcRefreshKey((k) => k + 1);
      navigate('/tenant/settings?tab=hmrc', { replace: true });
    } else if (hmrcError) {
      toastError('HMRC connection failed', decodeURIComponent(hmrcError));
      navigate('/tenant/settings?tab=hmrc', { replace: true });
    }
  }, [searchParams, navigate, toastSuccess, toastError]);

  const handleStartBillingCheckout = async () => {
    try {
      setBillingCheckoutLoading(true);
      setSubscriptionError('');
      const data = await subscriptionsAPI.createCheckout({
        billingCycle: subscriptionBillingCycle === 'annual' ? 'annual' : 'monthly',
      });
      if (data?.url) window.location.href = data.url;
      else setSubscriptionError('Could not start checkout');
    } catch (err) {
      setSubscriptionError(err.response?.data?.error || err.message || 'Could not start checkout');
    } finally {
      setBillingCheckoutLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of the current billing period.')) {
      return;
    }

    try {
      setSubscriptionLoading(true);
      await api.post('/subscriptions/cancel');
      await fetchSubscription();
    } catch (err) {
      setSubscriptionError(err.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      await api.post('/subscriptions/reactivate');
      await fetchSubscription();
    } catch (err) {
      setSubscriptionError(err.response?.data?.error || 'Failed to reactivate subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setSubscriptionLoading(true);
      const response = await api.post('/subscriptions/update-payment-method');
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setSubscriptionError(err.response?.data?.error || 'Failed to open payment portal');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const updateField = (key) => (e) => {
    const value = e.target.value;
    setSettings((prev) => ({ ...prev, [key]: value }));
    if (key === 'logo_url' && value) {
      setLogoPreview(value);
      setLogoFile(null);
    }
    if (key === 'accrediting_body_logo_url') {
      if (value) {
        setAccreditingLogoPreview(value);
        setAccreditingLogoFile(null);
      } else {
        revokeAccreditingLogoBlobUrl();
        setAccreditingLogoPreview(null);
        setAccreditingLogoFile(null);
      }
    }
  };

  const updateInvoiceField = (key) => (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
    setInvoiceSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      // Clear logo_url when uploading a file
      setSettings((prev) => ({ ...prev, logo_url: '' }));
    }
  };

  const handleRemoveLogo = () => {
    revokeLogoBlobUrl();
    setLogoFile(null);
    setLogoPreview(null);
    setSettings((prev) => ({ ...prev, logo_url: '' }));
  };

  const handleAccreditingLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setAccreditingLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAccreditingLogoPreview(reader.result);
      reader.readAsDataURL(file);
      setSettings((prev) => ({ ...prev, accrediting_body_logo_url: '' }));
    }
  };

  const handleRemoveAccreditingLogo = () => {
    revokeAccreditingLogoBlobUrl();
    setAccreditingLogoFile(null);
    setAccreditingLogoPreview(null);
    setSettings((prev) => ({ ...prev, accrediting_body_logo_url: '' }));
  };

  const handleImportFileSelect = (file) => {
    // Validate file type
    const allowedExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      setError('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }
    
    setImportFile(file);
    setError(null);
    setImportStep('upload');
    setImportMappings(null);
    setImportPreviewData(null);
    setImportResults(null);
  };

  const handleImportUpload = async () => {
    if (!importFile || (importType !== 'clients' && importType !== 'client-contacts' && importType !== 'suppliers')) {
      setError('Please select a file and ensure Clients, Client Contacts, or Suppliers is selected as the import type');
      return;
    }

    setImportLoading(true);
    setError(null);
    setSuccess('');

    try {
      let result;
      if (importType === 'clients') {
        result = await clientsAPI.importPreview(importFile, null);
      } else if (importType === 'client-contacts') {
        result = await clientsAPI.importContactsPreview(importFile, null);
      } else if (importType === 'suppliers') {
        result = await suppliersAPI.importPreview(importFile, null);
      }
      setImportMappings(result.mappings);
      setImportPreviewData(result);
      setImportStep('mapping');
    } catch (err) {
      setError(err.message || 'Failed to parse file. Please check the file format.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleMappingsChange = (mappings) => {
    setImportMappings(mappings);
  };

  const handleAutoDetect = async () => {
    if (!importFile) return;

    setImportLoading(true);
    setError(null);

    try {
      let result;
      if (importType === 'clients') {
        result = await clientsAPI.importPreview(importFile, null);
      } else if (importType === 'client-contacts') {
        result = await clientsAPI.importContactsPreview(importFile, null);
      } else if (importType === 'suppliers') {
        result = await suppliersAPI.importPreview(importFile, null);
      }
      setImportMappings(result.mappings);
      setImportPreviewData(result);
    } catch (err) {
      setError(err.message || 'Failed to re-detect mappings');
    } finally {
      setImportLoading(false);
    }
  };

  const handleApplyMappings = async () => {
    if (!importFile || !importMappings) return;

    setImportLoading(true);
    setError(null);

    try {
      let result;
      if (importType === 'clients') {
        result = await clientsAPI.importPreview(importFile, importMappings);
      } else if (importType === 'client-contacts') {
        result = await clientsAPI.importContactsPreview(importFile, importMappings);
      } else if (importType === 'suppliers') {
        result = await suppliersAPI.importPreview(importFile, importMappings);
      }
      setImportPreviewData(result);
      setImportStep('preview');
    } catch (err) {
      setError(err.message || 'Failed to apply mappings');
    } finally {
      setImportLoading(false);
    }
  };

  const handleRowsChange = (rows) => {
    setImportPreviewData(prev => ({
      ...prev,
      rows
    }));
  };

  const handleImportExecute = async (rowsToImport) => {
    setImportLoading(true);
    setError(null);
    setSuccess('');

    try {
      let result;
      if (importType === 'clients') {
        result = await clientsAPI.importExecute(rowsToImport);
        setSuccess(`Successfully imported ${result.imported} client(s)`);
      } else if (importType === 'client-contacts') {
        result = await clientsAPI.importContactsExecute(rowsToImport);
        setSuccess(`Successfully imported ${result.imported} contact(s)`);
      } else if (importType === 'suppliers') {
        result = await suppliersAPI.importExecute(rowsToImport);
        setSuccess(`Successfully imported ${result.imported} supplier(s)`);
      }
      setImportResults(result);
      setImportStep('results');
    } catch (err) {
      const typeName = importType === 'clients' ? 'clients' : importType === 'client-contacts' ? 'contacts' : 'suppliers';
      setError(err.message || `Failed to import ${typeName}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleSeedChartOfAccounts = async () => {
    if (!window.confirm('This will seed the Chart of Accounts for your tenant. This action is safe to run multiple times. Continue?')) {
      return;
    }

    setSeedingLoading(true);
    setError(null);
    setSuccess('');

    try {
      await accountingAPI.seedChartOfAccounts();
      setSuccess('Chart of Accounts seeded successfully!');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to seed Chart of Accounts');
    } finally {
      setSeedingLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    let template;
    let filename;
    
    if (importType === 'clients') {
      template = [
        ['Company Name', 'First Name', 'Last Name', 'Email', 'Phone', 'Mobile', 'Address Line 1', 'Address Line 2', 'City', 'County', 'Postcode', 'Country', 'Credit Limit', 'Notes'],
        ['Acme Corp', '', '', 'contact@acme.com', '01234567890', '', '123 Main St', '', 'London', 'Greater London', 'SW1A 1AA', 'GB', '1000', 'Important client'],
        ['', 'John', 'Doe', 'john@example.com', '01234567891', '07123456789', '456 High St', 'Apt 2', 'Manchester', 'Greater Manchester', 'M1 1AA', 'GB', '', 'Individual client']
      ];
      filename = 'client-import-template.csv';
    } else if (importType === 'client-contacts') {
      template = [
        ['Client Company Name', 'Contact First Name', 'Contact Last Name', 'Contact Email', 'Contact Phone', 'Contact Mobile', 'Position', 'Notes'],
        ['Acme Corp', 'Jane', 'Smith', 'jane@acme.com', '01234567890', '07123456789', 'Manager', 'Primary contact'],
        ['Acme Corp', 'Bob', 'Jones', 'bob@acme.com', '01234567891', '', 'Assistant', 'Secondary contact'],
        ['Tech Solutions Ltd', 'Alice', 'Brown', 'alice@tech.com', '01234567892', '', 'Director', '']
      ];
      filename = 'client-contacts-import-template.csv';
    } else if (importType === 'suppliers') {
      template = [
        ['Supplier Name', 'Supplier Code', 'Contact Name', 'Email', 'Phone', 'Mobile', 'Address Line 1', 'Address Line 2', 'City', 'County', 'Postcode', 'Country', 'Payment Terms', 'Credit Limit', 'Account Number', 'Notes'],
        ['ABC Supplies Ltd', 'ABC001', 'John Smith', 'john@abc.com', '01234567890', '07123456789', '123 Trade St', '', 'Birmingham', 'West Midlands', 'B1 1AA', 'GB', 'Net 30', '5000', 'ACC123', 'Main supplier'],
        ['XYZ Components', 'XYZ002', 'Sarah Jones', 'sarah@xyz.com', '01234567891', '', '456 Industrial Way', '', 'Manchester', 'Greater Manchester', 'M1 1BB', 'GB', 'Payment on delivery', '', '', 'Electrical components']
      ];
      filename = 'supplier-import-template.csv';
    } else {
      return;
    }
    
    const csv = template.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess('');
    try {
      let res;
      const settingsToSave = {
        ...settings,
        preferences: {
          ...(settings.preferences || {}),
          invoice: invoiceSettings,
        },
      };

      if (logoFile || accreditingLogoFile) {
        const formData = new FormData();
        if (logoFile) formData.append('logo', logoFile);
        if (accreditingLogoFile) formData.append('accrediting_logo', accreditingLogoFile);
        Object.keys(settingsToSave).forEach((key) => {
          if (key === 'logo_url' && logoFile) return;
          if (key === 'accrediting_body_logo_url' && accreditingLogoFile) return;
          if (key === 'preferences' || key === 'feature_flags') {
            formData.append(key, JSON.stringify(settingsToSave[key]));
          } else {
            formData.append(key, settingsToSave[key]);
          }
        });
        res = await fetch('/api/settings', {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
      } else {
        // Regular JSON submission
        res = await fetch('/api/settings', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(settingsToSave),
        });
      }
      
      if (!res.ok) throw new Error('Failed to save settings');
      const data = await res.json();
      
      if (data.settings?.logo_url) {
        const preview = await resolveLogoPreview(data.settings.logo_url);
        if (preview) setLogoPreview(preview);
        setSettings((prev) => ({ ...prev, logo_url: data.settings.logo_url }));
      } else {
        revokeLogoBlobUrl();
        setLogoPreview(null);
        setSettings((prev) => ({ ...prev, logo_url: '' }));
      }
      if (data.settings?.accrediting_body_logo_url) {
        const preview = await resolveAccreditingLogoPreview(data.settings.accrediting_body_logo_url);
        if (preview) setAccreditingLogoPreview(preview);
        setSettings((prev) => ({ ...prev, accrediting_body_logo_url: data.settings.accrediting_body_logo_url }));
      } else {
        revokeAccreditingLogoBlobUrl();
        setAccreditingLogoPreview(null);
        setSettings((prev) => ({ ...prev, accrediting_body_logo_url: '' }));
      }
      setLogoFile(null);
      setAccreditingLogoFile(null);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInvoiceSettingsSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess('');
    try {
      const settingsToSave = {
        ...settings,
        preferences: {
          ...(settings.preferences || {}),
          invoice: invoiceSettings,
        },
      };

      const res = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settingsToSave),
      });
      
      if (!res.ok) throw new Error('Failed to save invoice settings');
      const data = await res.json();
      
      // Update settings with saved preferences
      if (data.settings?.preferences) {
        setSettings((prev) => ({ ...prev, preferences: data.settings.preferences }));
      }
      
      setSuccess('Invoice settings saved successfully');
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to save invoice settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInvoiceStyleSave = async (invoiceStyle) => {
    setSaving(true);
    setError(null);
    setSuccess('');
    try {
      const settingsToSave = {
        ...settings,
        preferences: {
          ...(settings.preferences || {}),
          invoice: {
            ...(settings.preferences?.invoice || {}),
            ...invoiceSettings,
            invoice_style: invoiceStyle,
          },
        },
      };

      const res = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settingsToSave),
      });
      
      if (!res.ok) throw new Error('Failed to save invoice style');
      const data = await res.json();
      
      if (data.settings?.preferences) {
        setSettings((prev) => ({ ...prev, preferences: data.settings.preferences }));
        setInvoiceSettings((prev) => ({
          ...prev,
          invoice_style: data.settings.preferences?.invoice?.invoice_style || prev.invoice_style,
        }));
      }
      
      setSuccess('Invoice style saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to save invoice style');
    } finally {
      setSaving(false);
    }
  };

  const handleAISettingsSubmit = async (e) => {
    e.preventDefault();
    setAiSettingsSaving(true);
    setError(null);
    setSuccess('');

    try {
      const updateData = {
        provider: aiSettings.provider,
        model: aiSettings.model,
      };

      // Only include API key if it was changed (not empty)
      if (apiKeyInput.trim() !== '') {
        updateData.apiKey = apiKeyInput.trim();
      }

      await settingsAPI.updateAI(updateData);

      // Reload AI settings to get updated state
      const updated = await settingsAPI.getAI();
      setAiSettings(updated);
      setApiKeyInput(''); // Clear the input after successful save
      setShowApiKey(false);

      setSuccess('AI settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to save AI settings');
    } finally {
      setAiSettingsSaving(false);
    }
  };

  const isValidSumUpApiKey = (value) => /^sup_(sk|test)_[a-zA-Z0-9]+$/.test((value || '').trim());

  const handlePaymentsSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess('');

    const activeGateway = paymentsData?.activeGateway ?? 'stripe';

    if (activeGateway === 'sumup' && sumupApiKeyDirty && sumupApiKeyInput.trim()) {
      if (!isValidSumUpApiKey(sumupApiKeyInput)) {
        setError('Invalid SumUp API key. It should start with sup_sk_ (production) or sup_test_ (sandbox).');
        return;
      }
    }

    setPaymentsSaving(true);
    try {
      const updateData = {
        payEnabled: paymentsData?.payEnabled ?? true,
        activeGateway: activeGateway === 'sumup' ? 'sumup' : 'stripe',
      };
      if (stripeSecretDirty) {
        updateData.stripe = updateData.stripe || {};
        updateData.stripe.secretKey = stripeSecretInput.trim() || null;
      }
      if (stripeWebhookDirty) {
        updateData.stripe = updateData.stripe || {};
        updateData.stripe.webhookSecret = stripeWebhookInput.trim() || null;
      }
      if (sumupApiKeyDirty) {
        updateData.sumup = updateData.sumup || {};
        updateData.sumup.apiKey = sumupApiKeyInput.trim() || null;
      }
      if (sumupWebhookDirty) {
        updateData.sumup = updateData.sumup || {};
        updateData.sumup.webhookSecret = sumupWebhookInput.trim() || null;
      }
      await settingsAPI.updatePayments(updateData);

      const updated = await settingsAPI.getPayments();
      setPaymentsData(updated);
      setStripeSecretInput('');
      setStripeWebhookInput('');
      setStripeSecretDirty(false);
      setStripeWebhookDirty(false);
      setSumupApiKeyInput('');
      setSumupWebhookInput('');
      setSumupApiKeyDirty(false);
      setSumupWebhookDirty(false);

      setSuccess('Payments settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to save payments settings');
    } finally {
      setPaymentsSaving(false);
    }
  };

  const isValidGoogleClientId = (value) => /\.apps\.googleusercontent\.com$/.test((value || '').trim());

  const handleGoogleCalendarSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess('');

    const clientIdVal = googleClientIdDirty ? googleClientIdInput.trim() : null;
    const clientSecretVal = googleClientSecretDirty ? googleClientSecretInput.trim() : null;

    if (clientIdVal !== null && clientIdVal !== '' && !isValidGoogleClientId(clientIdVal)) {
      setError('Invalid Google Client ID. It should end with .apps.googleusercontent.com');
      return;
    }

    setGoogleCalendarSaving(true);
    try {
      const updateData = {};
      if (googleClientIdDirty) updateData.clientId = clientIdVal || null;
      if (googleClientSecretDirty) updateData.clientSecret = clientSecretVal || null;

      if (Object.keys(updateData).length > 0) {
        await settingsAPI.updateGoogleCalendarIntegration(updateData);
      }

      const updated = await settingsAPI.getGoogleCalendarIntegration();
      setGoogleCalendarData(updated);
      setGoogleClientIdInput('');
      setGoogleClientSecretInput('');
      setGoogleClientIdDirty(false);
      setGoogleClientSecretDirty(false);

      setSuccess('Google Calendar integration saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to save Google Calendar integration');
    } finally {
      setGoogleCalendarSaving(false);
    }
  };

  const currentWebhookUrl = paymentsData?.webhookUrls
    ? (paymentsData.activeGateway === 'sumup' ? paymentsData.webhookUrls.sumup : paymentsData.webhookUrls.stripe)
    : paymentsData?.webhookUrl;

  const handleCopyWebhookUrl = async () => {
    if (!currentWebhookUrl) return;
    try {
      await navigator.clipboard.writeText(currentWebhookUrl);
      setError(null);
      setSuccess('Webhook URL copied to clipboard');
      setTimeout(() => setSuccess(''), 2000);
    } catch {
      setSuccess('');
      setError('Failed to copy to clipboard');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-gray-700 dark:text-gray-300">Loading settings...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Settings
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                {isSettingsHub
                  ? 'Choose a category, then open the section you need.'
                  : detailTabValid
                    ? SETTINGS_DETAIL_LABELS[tabParam]
                    : 'Manage your tenant settings'}
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/dashboard')}
              >
                ← Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isSettingsHub ? (
          <Card className="mb-6">
            <CardContent className="p-4 sm:p-6">
              <div
                className="flex flex-col sm:flex-row gap-1 sm:gap-1 p-1 rounded-[var(--ds-radius-md)] bg-[var(--ds-control-field-bg)] mb-6"
                role="tablist"
                aria-label="Settings categories"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={hubSection === 'general'}
                  onClick={() => setHubSection('general')}
                  className={`flex-1 rounded-[var(--ds-radius-sm)] px-3 py-2.5 text-body font-medium transition-colors duration-180 ${
                    hubSection === 'general'
                      ? 'bg-[var(--ds-surface-panel)] text-[var(--ds-text-primary)] shadow-[var(--ds-shadow-card)] border border-[var(--ds-border-subtle)]'
                      : 'text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]'
                  }`}
                >
                  General
                </button>
                {isTenantAdmin && (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={hubSection === 'team'}
                    onClick={() => setHubSection('team')}
                    className={`flex-1 rounded-[var(--ds-radius-sm)] px-3 py-2.5 text-body font-medium transition-colors duration-180 ${
                      hubSection === 'team'
                        ? 'bg-[var(--ds-surface-panel)] text-[var(--ds-text-primary)] shadow-[var(--ds-shadow-card)] border border-[var(--ds-border-subtle)]'
                        : 'text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]'
                    }`}
                  >
                    Team
                  </button>
                )}
                <button
                  type="button"
                  role="tab"
                  aria-selected={hubSection === 'integrations'}
                  onClick={() => setHubSection('integrations')}
                  className={`flex-1 rounded-[var(--ds-radius-sm)] px-3 py-2.5 text-body font-medium transition-colors duration-180 ${
                    hubSection === 'integrations'
                      ? 'bg-[var(--ds-surface-panel)] text-[var(--ds-text-primary)] shadow-[var(--ds-shadow-card)] border border-[var(--ds-border-subtle)]'
                      : 'text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]'
                  }`}
                >
                  Integrations
                </button>
              </div>

              {hubSection === 'general' && (
                <div className="space-y-2">
                  {[
                    {
                      to: '/tenant/settings?tab=company',
                      icon: Building2,
                      title: 'Company settings',
                      desc: 'Branding, address, and contact details',
                    },
                    {
                      to: '/tenant/settings?tab=subscription',
                      icon: CreditCard,
                      title: 'Manage subscription',
                      desc: 'Plan, billing, and payment method',
                    },
                    {
                      to: '/tenant/settings?tab=invoice',
                      icon: FileText,
                      title: 'Invoice settings',
                      desc: 'Defaults, style, and client email template',
                    },
                    {
                      to: '/tenant/settings?tab=routine-emails',
                      icon: Mail,
                      title: 'Routine emails',
                      desc: 'Reminders and automated message templates',
                    },
                    {
                      to: '/tenant/settings?tab=import',
                      icon: FileSpreadsheet,
                      title: 'Import data',
                      desc: 'Import clients, suppliers, and catalogue',
                    },
                    {
                      to: '/tenant/settings?tab=backup-export',
                      icon: HardDrive,
                      title: 'Backup / Export Data',
                      desc: 'Download your tenant data as CSV in a ZIP archive',
                    },
                    {
                      to: '/tenant/settings?tab=accounting',
                      icon: BookOpen,
                      title: 'Accounting',
                      desc: 'Chart of accounts and journals',
                    },
                    ...(supportsPasskey
                      ? [
                          {
                            to: '/tenant/settings?tab=security',
                            icon: Fingerprint,
                            title: 'Security',
                            desc: 'Passkeys and sign-in security',
                          },
                        ]
                      : []),
                  ].map(({ to, icon: Icon, title, desc }) => (
                    <Link
                      key={to}
                      to={to}
                      className="flex items-center gap-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-4 transition-colors duration-180 hover:bg-[var(--ds-control-field-hover-bg)]"
                    >
                      <Icon className="h-5 w-5 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
                      <p className="flex-1 min-w-0 text-body leading-snug text-[var(--ds-text-primary)] break-words">
                        <span className="font-medium">{title}</span>
                        <span className="font-normal text-[var(--ds-text-muted)]"> - </span>
                        <span className="font-normal text-meta text-[var(--ds-text-muted)]">{desc}</span>
                      </p>
                      <ChevronRight className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                    </Link>
                  ))}
                </div>
              )}

              {hubSection === 'team' && isTenantAdmin && (
                <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <Users className="h-8 w-8 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
                    <div>
                      <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Team</h2>
                      <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
                        Invite staff, set roles, and manage who can access your tenant.
                      </p>
                    </div>
                  </div>
                  <Button type="button" onClick={() => navigate('/tenant/users')}>
                    Open team management
                  </Button>
                </div>
              )}

              {hubSection === 'integrations' && (
                <div className="space-y-2">
                  <Link
                    to="/tenant/settings?tab=ai"
                    className="flex items-center gap-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-4 transition-colors duration-180 hover:bg-[var(--ds-control-field-hover-bg)]"
                  >
                    <Sparkles className="h-5 w-5 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
                    <p className="flex-1 min-w-0 text-body leading-snug text-[var(--ds-text-primary)] break-words">
                      <span className="font-medium">AI integrations</span>
                      <span className="font-normal text-[var(--ds-text-muted)]"> - </span>
                      <span className="font-normal text-meta text-[var(--ds-text-muted)]">
                        API keys and models for purchase AI features
                      </span>
                    </p>
                    <ChevronRight className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                  </Link>
                  <Link
                    to="/tenant/settings?tab=payments"
                    className="flex items-center gap-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-4 transition-colors duration-180 hover:bg-[var(--ds-control-field-hover-bg)]"
                  >
                    <Plug className="h-5 w-5 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
                    <p className="flex-1 min-w-0 text-body leading-snug text-[var(--ds-text-primary)] break-words">
                      <span className="font-medium">Payments</span>
                      <span className="font-normal text-[var(--ds-text-muted)]"> - </span>
                      <span className="font-normal text-meta text-[var(--ds-text-muted)]">
                        Stripe, SumUp, and client payment options
                      </span>
                    </p>
                    <ChevronRight className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                  </Link>
                  <Link
                    to="/tenant/settings?tab=hmrc"
                    className="flex items-center gap-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-4 transition-colors duration-180 hover:bg-[var(--ds-control-field-hover-bg)]"
                  >
                    <Landmark className="h-5 w-5 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
                    <p className="flex-1 min-w-0 text-body leading-snug text-[var(--ds-text-primary)] break-words">
                      <span className="font-medium">HMRC Making Tax Digital</span>
                      <span className="font-normal text-[var(--ds-text-muted)]"> - </span>
                      <span className="font-normal text-meta text-[var(--ds-text-muted)]">
                        VAT MTD OAuth connection (sandbox and live)
                      </span>
                    </p>
                    <ChevronRight className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                  </Link>
                  <Link
                    to="/tenant/settings?tab=integrations"
                    className="flex items-center gap-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-4 transition-colors duration-180 hover:bg-[var(--ds-control-field-hover-bg)]"
                  >
                    <Calendar className="h-5 w-5 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
                    <p className="flex-1 min-w-0 text-body leading-snug text-[var(--ds-text-primary)] break-words">
                      <span className="font-medium">Calendar &amp; other integrations</span>
                      <span className="font-normal text-[var(--ds-text-muted)]"> - </span>
                      <span className="font-normal text-meta text-[var(--ds-text-muted)]">
                        Google Calendar and connected services
                      </span>
                    </p>
                    <ChevronRight className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                  </Link>
                  <div className="flex items-center gap-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)] p-4 opacity-70 cursor-not-allowed">
                    <Landmark className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                    <p className="flex-1 min-w-0 text-body leading-snug text-[var(--ds-text-primary)] break-words">
                      <span className="font-medium">Banking feeds</span>
                      <span className="font-normal text-[var(--ds-text-muted)]"> - </span>
                      <span className="font-normal text-meta text-[var(--ds-text-muted)]">Future development</span>
                    </p>
                    <span className="text-micro font-medium text-[var(--ds-text-muted)] shrink-0">Soon</span>
                  </div>
                  <div className="flex items-center gap-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)] p-4 opacity-70 cursor-not-allowed">
                    <Mail className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                    <p className="flex-1 min-w-0 text-body leading-snug text-[var(--ds-text-primary)] break-words">
                      <span className="font-medium">SMTP settings</span>
                      <span className="font-normal text-[var(--ds-text-muted)]"> - </span>
                      <span className="font-normal text-meta text-[var(--ds-text-muted)]">Future development</span>
                    </p>
                    <span className="text-micro font-medium text-[var(--ds-text-muted)] shrink-0">Soon</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="mb-6">
            <Button variant="outline" size="sm" type="button" onClick={goToSettingsHub} className="gap-2">
              <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
              All settings
            </Button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)]">
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-4 bg-[var(--ds-status-success-bg)] border border-[var(--ds-status-success)]/30 text-[var(--ds-status-success)] px-4 py-3 rounded-[var(--ds-radius-md)]">
            {success}
          </div>
        )}

        {/* Company Settings Tab */}
        {!isSettingsHub && activeTab === 'company' && (
          <Card>
            <CardContent className="p-6">
            <div className="mb-6">
              <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Company Settings & Branding</h2>
              <p className="text-xs text-[var(--ds-text-secondary)] mt-1">Update your company branding and contact details for documents and emails.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Company Details */}
              <div className="space-y-6">
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
                  <Input
                    type="text"
                    value={settings.company_name || ''}
                    onChange={updateField('company_name')}
                    className="h-8"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Company Reg No</label>
                    <Input
                      type="text"
                      value={settings.company_reg_no || ''}
                      onChange={updateField('company_reg_no')}
                      placeholder="e.g. 12345678"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Company VAT No</label>
                    <Input
                      type="text"
                      value={settings.company_vat_no || ''}
                      onChange={updateField('company_vat_no')}
                      placeholder="e.g. GB123456789"
                      className="h-8"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column: Company Logo */}
              <div className="lg:border-l lg:border-[var(--ds-border-divider)] lg:pl-6">
                <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-2">Company Logo</label>
                
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  {logoPreview && (
                    <div className="relative inline-block flex-shrink-0">
                      <img
                        src={logoPreview}
                        alt="Company logo preview"
                        className="h-24 w-24 object-contain border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] bg-[var(--ds-surface-panel)] p-2"
                      />
                      <TooltipProvider>
                        <Tooltip content="Remove logo">
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            className="absolute -bottom-2 -right-2 h-6 w-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            aria-label="Remove logo"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}

                  {/* File Upload Controls */}
                  <div className="flex-1">
                    <div className="flex flex-col gap-4">
                      <label className="inline-flex items-center justify-center px-4 py-2 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] text-xs font-medium text-[var(--ds-text-primary)] bg-[var(--ds-surface-panel)] hover:bg-[var(--ds-control-field-bg)] cursor-pointer w-full">
                        <Upload className="h-4 w-4 mr-2" />
                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoFileChange}
                          className="hidden"
                        />
                      </label>
                      <Input
                        type="url"
                        value={settings.logo_url || ''}
                        onChange={updateField('logo_url')}
                        placeholder="Enter logo URL"
                        className="h-8 w-full"
                        onFocus={() => {
                          // Clear file when focusing on URL input
                          if (logoFile) {
                            setLogoFile(null);
                          }
                        }}
                      />
                    </div>
                    <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                      Upload an image file (max 5MB) or enter a URL. Recommended size: 200x200px or larger.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Address Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Address Line 1</label>
                  <Input
                    type="text"
                    value={settings.address_line1 || ''}
                    onChange={updateField('address_line1')}
                    className="h-8 w-full"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Address Line 2</label>
                  <Input
                    type="text"
                    value={settings.address_line2 || ''}
                    onChange={updateField('address_line2')}
                    className="h-8 w-full"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                  <Input
                    type="text"
                    value={settings.city || ''}
                    onChange={updateField('city')}
                    className="h-8 w-full"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">County</label>
                  <Input
                    type="text"
                    value={settings.county || ''}
                    onChange={updateField('county')}
                    className="h-8 w-full"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Postcode</label>
                  <Input
                    type="text"
                    value={settings.postcode || ''}
                    onChange={updateField('postcode')}
                    className="h-8 w-full"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                  <Input
                    type="text"
                    value={settings.phone || ''}
                    onChange={updateField('phone')}
                    className="h-8 w-full"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <Input
                    type="email"
                    value={settings.email || ''}
                    onChange={updateField('email')}
                    className="h-8 w-full"
                  />
                </div>
              </div>

              {/* Right Column: Styling */}
              <div className="space-y-4 lg:border-l lg:border-[var(--ds-border-divider)] lg:pl-6">
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Colour</label>
                  <div className="relative flex items-center">
                    <div
                      className="absolute left-3 h-6 w-6 rounded border border-[var(--ds-border-subtle)]"
                      style={{ backgroundColor: settings.primary_color || '#3B82F6' }}
                    />
                    <Input
                      type="text"
                      value={settings.primary_color || ''}
                      onChange={updateField('primary_color')}
                      placeholder="#3B82F6"
                      className="h-8 pl-12"
                    />
                  </div>
                  <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                    Used for primary buttons and accents
                  </p>
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Secondary Colour</label>
                  <div className="relative flex items-center">
                    <div
                      className="absolute left-3 h-6 w-6 rounded border border-[var(--ds-border-subtle)]"
                      style={{ backgroundColor: settings.secondary_color || '#10B981' }}
                    />
                    <Input
                      type="text"
                      value={settings.secondary_color || ''}
                      onChange={updateField('secondary_color')}
                      placeholder="#10B981"
                      className="h-8 pl-12"
                    />
                  </div>
                  <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                    Used for secondary elements and highlights
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[var(--ds-border-divider)]">
              <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">Professional enrolment</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Accrediting body</label>
                    <Select
                      value={settings.accrediting_body || ''}
                      onChange={(e) => setSettings((prev) => ({ ...prev, accrediting_body: e.target.value }))}
                      className="h-8"
                    >
                      <option value="">None</option>
                      <option value="Napit">Napit</option>
                      <option value="NICEIC">NICEIC</option>
                      <option value="ECA">ECA</option>
                      <option value="Stroma">Stroma</option>
                      <option value="Blueflame">Blueflame</option>
                      <option value="other">Other (Please specify)</option>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Enrolment No</label>
                    <Input
                      type="text"
                      value={settings.enrolment_no || ''}
                      onChange={updateField('enrolment_no')}
                      placeholder="e.g. 12345"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Branch</label>
                    <Input
                      type="text"
                      value={settings.branch || ''}
                      onChange={updateField('branch')}
                      placeholder="e.g. London"
                      className="h-8"
                    />
                  </div>
                </div>
                {(settings.accrediting_body || '') === 'other' && (
                  <div>
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Other</label>
                    <Input
                      type="text"
                      value={settings.accrediting_body_other || ''}
                      onChange={updateField('accrediting_body_other')}
                      placeholder="Specify accrediting body"
                      className="h-8"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-2">Accrediting body logo</label>
                  {accreditingLogoPreview && (
                    <div className="mb-4 flex items-center gap-4">
                      <img
                        src={accreditingLogoPreview}
                        alt="Accrediting body logo preview"
                        className="h-24 w-24 object-contain border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] bg-[var(--ds-surface-panel)] p-2"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={handleRemoveAccreditingLogo}
                        className="text-[var(--ds-status-danger)] border-[var(--ds-status-danger)]/30 hover:bg-[var(--ds-status-danger-bg)]"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center px-4 py-2 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] text-xs font-medium text-[var(--ds-text-primary)] bg-[var(--ds-surface-panel)] hover:bg-[var(--ds-control-field-bg)] cursor-pointer whitespace-nowrap flex-shrink-0">
                      <Upload className="h-4 w-4 mr-2" />
                      {accreditingLogoPreview ? 'Change logo' : 'Upload logo'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAccreditingLogoFileChange}
                        className="hidden"
                      />
                    </label>
                    <span className="text-xs text-[var(--ds-text-muted)] flex-shrink-0">or</span>
                    <Input
                      type="url"
                      value={settings.accrediting_body_logo_url || ''}
                      onChange={updateField('accrediting_body_logo_url')}
                      placeholder="Enter logo URL"
                      className="h-8 max-w-md"
                      onFocus={() => accreditingLogoFile && setAccreditingLogoFile(null)}
                    />
                  </div>
                  <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                    Upload or provide a URL for your accrediting body logo. Used on invoices and documents. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </form>
            </CardContent>
          </Card>
        )}

        {/* Subscription Tab */}
        {!isSettingsHub && activeTab === 'subscription' && (
          <Card>
            <CardContent className="p-6">
            <div className="mb-6">
              <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Subscription Management</h2>
              <p className="text-xs text-[var(--ds-text-secondary)] mt-1">Manage your subscription, billing, and payment methods.</p>
            </div>

            {subscriptionError && (
              <div className="mb-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)]">
                {subscriptionError}
              </div>
            )}

            {subscriptionLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-accent-primary)]" />
              </div>
            ) : subscription ? (
              <div className="space-y-6">
                {(() => {
                  const linked = hasLinkedStripeSubscription(subscription);
                  const needsBillingCheckout =
                    !linked &&
                    (subscription.status === 'trialing' || Boolean(subscription.trialEndsAt));
                  if (!needsBillingCheckout) return null;
                  return (
                    <div className="bg-[var(--ds-status-warning-bg)] border border-[var(--ds-status-warning)]/30 rounded-[var(--ds-radius-md)] p-4">
                      <div className="flex items-start gap-3">
                        <CreditCard className="h-5 w-5 text-[var(--ds-status-warning)] shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[var(--ds-text-primary)]">
                            Add a payment method
                          </p>
                          <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                            You are on a trial without billing on file. Add a card to keep your
                            workspace running after the trial ends.
                          </p>
                          {isTenantAdmin ? (
                            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
                              <div className="flex rounded-md border border-[var(--ds-control-field-border)] overflow-hidden w-fit">
                                <button
                                  type="button"
                                  onClick={() => setSubscriptionBillingCycle('monthly')}
                                  className={`px-3 py-1.5 text-xs font-medium ${
                                    subscriptionBillingCycle === 'monthly'
                                      ? 'bg-[var(--ds-accent-primary)] text-white'
                                      : 'bg-[var(--ds-surface-panel2)] text-[var(--ds-text-primary)]'
                                  }`}
                                >
                                  Monthly
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSubscriptionBillingCycle('annual')}
                                  className={`px-3 py-1.5 text-xs font-medium border-l border-[var(--ds-control-field-border)] ${
                                    subscriptionBillingCycle === 'annual'
                                      ? 'bg-[var(--ds-accent-primary)] text-white'
                                      : 'bg-[var(--ds-surface-panel2)] text-[var(--ds-text-primary)]'
                                  }`}
                                >
                                  Annual
                                </button>
                              </div>
                              <Button
                                size="sm"
                                type="button"
                                onClick={handleStartBillingCheckout}
                                disabled={billingCheckoutLoading || subscriptionLoading}
                              >
                                {billingCheckoutLoading ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Opening checkout…
                                  </>
                                ) : (
                                  <>
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Activate billing
                                  </>
                                )}
                              </Button>
                            </div>
                          ) : (
                            <p className="text-xs text-[var(--ds-text-muted)] mt-2">
                              Ask your account owner to complete billing in this tab.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {/* Current Plan */}
                <Card className="bg-[var(--ds-surface-panel2)]">
                  <CardContent className="p-6">
                  <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">Current Plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[var(--ds-text-muted)]">Package</p>
                      <p className="text-h2 font-semibold text-[var(--ds-text-primary)]">
                        {subscription.packageName || 'No package'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--ds-text-muted)]">Status</p>
                      <div className="flex items-center">
                        {subscription.status === 'active' || subscription.status === 'trialing' ? (
                          <CheckCircle className="h-5 w-5 text-[var(--ds-status-success)] mr-2" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-[var(--ds-status-danger)] mr-2" />
                        )}
                        <p className="text-h2 font-semibold text-[var(--ds-text-primary)] capitalize">
                          {subscription.status || 'Unknown'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {subscription.packageDescription && (
                    <p className="text-xs text-[var(--ds-text-muted)] mt-2">
                      {subscription.packageDescription}
                    </p>
                  )}
                  </CardContent>
                </Card>

                {/* Billing Information */}
                {(subscription.currentPeriodEnd || subscription.trialEndsAt) && (
                  <Card className="bg-[var(--ds-surface-panel2)]">
                    <CardContent className="p-6">
                    <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">Billing Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {subscription.currentPeriodStart && (
                        <div>
                          <p className="text-xs text-[var(--ds-text-muted)]">Current Period Start</p>
                          <p className="text-xs font-medium text-[var(--ds-text-primary)]">
                            {new Date(subscription.currentPeriodStart).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                      )}
                      {subscription.currentPeriodEnd && (
                        <div>
                          <p className="text-xs text-[var(--ds-text-muted)]">Current Period End</p>
                          <p className="text-xs font-medium text-[var(--ds-text-primary)]">
                            {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                      )}
                      {subscription.trialEndsAt && (
                        <div>
                          <p className="text-xs text-[var(--ds-text-muted)]">Trial Ends</p>
                          <p className="text-xs font-medium text-[var(--ds-accent-primary)]">
                            {new Date(subscription.trialEndsAt).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUpdatePaymentMethod}
                    disabled={
                      subscriptionLoading ||
                      !hasLinkedStripeSubscription(subscription)
                    }
                  >
                    <CreditCard className="h-5 w-5 mr-2" />
                    Update Payment Method
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                  {subscription.cancelAtPeriodEnd ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReactivateSubscription}
                      disabled={
                        subscriptionLoading || !hasLinkedStripeSubscription(subscription)
                      }
                      className="border-[var(--ds-status-success)] text-[var(--ds-status-success)] hover:bg-[var(--ds-status-success-bg)]"
                    >
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Reactivate Subscription
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelSubscription}
                      disabled={
                        subscriptionLoading ||
                        subscription.status === 'canceled' ||
                        !hasLinkedStripeSubscription(subscription)
                      }
                      className="border-[var(--ds-status-danger)] text-[var(--ds-status-danger)] hover:bg-[var(--ds-status-danger-bg)]"
                    >
                      <X className="h-5 w-5 mr-2" />
                      Cancel Subscription
                    </Button>
                  )}
                </div>

                {subscription.cancelAtPeriodEnd && (
                  <div className="bg-[var(--ds-status-warning-bg)] border border-[var(--ds-status-warning)]/30 rounded-[var(--ds-radius-md)] p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-[var(--ds-status-warning)] mr-2 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-[var(--ds-status-warning)]">
                          Subscription will be canceled
                        </p>
                        <p className="text-xs text-[var(--ds-status-warning)] mt-1">
                          Your subscription will remain active until {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-GB') : 'the end of the billing period'}.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="h-12 w-12 text-[var(--ds-text-muted)] mx-auto mb-4" />
                <p className="text-xs text-[var(--ds-text-muted)]">No subscription information available</p>
              </div>
            )}
            </CardContent>
          </Card>
        )}

        {/* Invoice Settings Tab */}
        {!isSettingsHub && activeTab === 'invoice' && (
          <Card>
            <CardContent className="p-6">
            <div className="mb-6">
              <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Invoice Settings</h2>
              <p className="text-xs text-[var(--ds-text-secondary)] mt-1">Configure default values for new invoices.</p>
            </div>

            {/* Invoice Settings Sub-tabs */}
            <div className="border-b border-[var(--ds-border-divider)] mb-6">
              <nav className="-mb-px flex">
                <button
                  type="button"
                  onClick={() => setInvoiceSettingsSubTab('general')}
                  className={`py-3 px-4 border-b-2 font-medium text-body ${
                    invoiceSettingsSubTab === 'general'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  General
                </button>
                <button
                  type="button"
                  onClick={() => setInvoiceSettingsSubTab('style')}
                  className={`py-3 px-4 border-b-2 font-medium text-body ${
                    invoiceSettingsSubTab === 'style'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  Style
                </button>
                <button
                  type="button"
                  onClick={() => setInvoiceSettingsSubTab('client-email')}
                  className={`py-3 px-4 border-b-2 font-medium text-body ${
                    invoiceSettingsSubTab === 'client-email'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  Client email
                </button>
              </nav>
            </div>

            {/* General Tab */}
            {invoiceSettingsSubTab === 'general' && (
            <form onSubmit={handleInvoiceSettingsSubmit} className="space-y-6">
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Payment Terms
                </label>
                <Input
                  type="text"
                  value={invoiceSettings.default_payment_terms || ''}
                  onChange={updateInvoiceField('default_payment_terms')}
                  placeholder="e.g., Net 30, Payment on delivery, Due on receipt"
                  className="h-8"
                />
                <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                  Default payment terms that will be applied to new invoices
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default VAT Rate (%)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={invoiceSettings.default_vat_rate ?? 20}
                    onChange={updateInvoiceField('default_vat_rate')}
                    disabled={invoiceSettings.default_vat_rate === 0}
                    className="h-8"
                  />
                  <label className="mt-2 flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invoiceSettings.default_vat_rate === 0}
                      onChange={(e) => setInvoiceSettings((prev) => ({
                        ...prev,
                        default_vat_rate: e.target.checked ? 0 : 20,
                      }))}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                    />
                    <span className="text-micro text-gray-700 dark:text-gray-300">Not VAT registered (use 0%)</span>
                  </label>
                  <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                    Default VAT rate for new invoices (UK standard: 20%)
                  </p>
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Payment Due Days
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={invoiceSettings.default_payment_due_days || 30}
                    onChange={updateInvoiceField('default_payment_due_days')}
                    className="h-8"
                  />
                  <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                    Number of days from issue date until payment is due
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Invoice Number Prefix
                  </label>
                  <Input
                    type="text"
                    value={invoiceSettings.invoice_number_prefix || ''}
                    onChange={updateInvoiceField('invoice_number_prefix')}
                    placeholder="e.g., INV-, SM-"
                    className="h-8"
                  />
                  <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                    Optional prefix for invoice numbers (e.g., "INV-" will create "INV-001", "INV-002", etc.)
                  </p>
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Starting Invoice Number
                  </label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={invoiceSettings.starting_invoice_number || 1}
                    onChange={updateInvoiceField('starting_invoice_number')}
                    className="h-8"
                  />
                  <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                    The starting number for new invoices (e.g., 1, 100, 1000)
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Invoice Notes
                </label>
                <MinimalRichTextEditor
                  value={invoiceSettings.default_invoice_notes || ''}
                  onChange={(html) =>
                    setInvoiceSettings((prev) => ({ ...prev, default_invoice_notes: html }))
                  }
                  rows={4}
                  placeholder="Default notes or terms that will appear on new invoices..."
                />
                <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                  Default notes or terms and conditions that will be pre-filled on new invoices
                </p>
              </div>

              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Invoice Terms & Conditions
                </label>
                <MinimalRichTextEditor
                  value={invoiceSettings.default_invoice_terms || ''}
                  onChange={(html) =>
                    setInvoiceSettings((prev) => ({ ...prev, default_invoice_terms: html }))
                  }
                  rows={6}
                  placeholder="Payment terms and conditions..."
                />
                <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                  Default terms & conditions that will be pre-filled in the Terms & Conditions section on new invoices
                </p>
              </div>

              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Invoice Settings'}
                </Button>
              </div>
            </form>
            )}

            {/* Style Tab */}
            {invoiceSettingsSubTab === 'style' && (
              <div className="py-4">
                <InvoiceStyleEditor
                  invoiceStyle={invoiceSettings.invoice_style}
                  primaryColor={settings.primary_color || '#3B82F6'}
                  secondaryColor={settings.secondary_color || '#10B981'}
                  onSave={handleInvoiceStyleSave}
                  saving={saving}
                />
              </div>
            )}

            {invoiceSettingsSubTab === 'client-email' && (
              <form onSubmit={handleInvoiceSettingsSubmit} className="space-y-6 py-1">
                <p className="text-xs text-[var(--ds-text-secondary)]">
                  Customise the email sent when you send an invoice or estimate link to a client. Leave all fields
                  empty to use the built-in SparxMate template. The same template applies to both invoices and
                  estimates; wording such as &quot;Invoice&quot; or &quot;Estimate&quot; is filled in automatically.
                </p>
                <p className="text-xs text-[var(--ds-text-secondary)]">
                  For the rich default used when sending without a custom message from the invoice editor, keep
                  editing here; for Send Invoice screen templates and reminders, use{' '}
                  <Link
                    to="/tenant/settings?tab=routine-emails"
                    className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Routine emails
                  </Link>
                  .
                </p>

                <div className="rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-4">
                  <p className="text-meta font-medium text-gray-700 dark:text-gray-300 mb-2">Placeholders</p>
                  <ul className="text-micro text-gray-600 dark:text-gray-400 space-y-1.5">
                    {CLIENT_EMAIL_PLACEHOLDERS.map(({ token, description }) => (
                      <li key={token} className="flex flex-wrap gap-x-2 gap-y-0.5">
                        <code className="shrink-0 rounded bg-white dark:bg-gray-700 px-1.5 py-0.5 text-gray-900 dark:text-gray-100 text-[11px]">
                          {token}
                        </code>
                        <span>{description}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subject line
                  </label>
                  <Input
                    type="text"
                    value={invoiceSettings.client_email_subject || ''}
                    onChange={(e) =>
                      setInvoiceSettings((prev) => ({ ...prev, client_email_subject: e.target.value }))
                    }
                    placeholder="{{documentLabel}} {{documentNumber}} from {{tenantName}}"
                    className="h-8 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Plain text body
                  </label>
                  <Textarea
                    value={invoiceSettings.client_email_text || ''}
                    onChange={(e) =>
                      setInvoiceSettings((prev) => ({ ...prev, client_email_text: e.target.value }))
                    }
                    placeholder="Hello {{clientName}}, ..."
                    className="min-h-[160px] resize-y font-mono text-xs border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    HTML body
                  </label>
                  <Textarea
                    value={invoiceSettings.client_email_html || ''}
                    onChange={(e) =>
                      setInvoiceSettings((prev) => ({ ...prev, client_email_html: e.target.value }))
                    }
                    placeholder="Full HTML email; include placeholders such as {{publicLink}} in your button href."
                    className="min-h-[280px] resize-y font-mono text-xs border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                    Use <code className="text-gray-800 dark:text-gray-200">{'{{publicLink}}'}</code> for the view
                    link (safe in HTML). Client and business names are escaped automatically in HTML.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <Button variant="outline" size="sm" type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Save client email template'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      setInvoiceSettings((prev) => ({
                        ...prev,
                        client_email_subject: '',
                        client_email_text: '',
                        client_email_html: '',
                      }))
                    }
                  >
                    Clear fields (use defaults)
                  </Button>
                </div>
              </form>
            )}
            </CardContent>
          </Card>
        )}

        {!isSettingsHub && activeTab === 'routine-emails' && (
          <RoutineEmailsSettings
            token={token}
            settings={settings}
            setSettings={setSettings}
            invoiceSettings={invoiceSettings}
            setSaving={setSaving}
            setError={setError}
            setSuccess={setSuccess}
            saving={saving}
            expandFromQuery={searchParams.get('expand')}
          />
        )}

        {/* Import Tab */}
        {!isSettingsHub && activeTab === 'import' && (
          <div className="space-y-6">
            {/* Step 1: Upload */}
            {importStep === 'upload' && (
              <Card>
                <CardContent className="p-6">
                  <div className="mb-6">
                    <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">
                      Import {importType === 'clients' ? 'Clients' : importType === 'client-contacts' ? 'Client Contacts' : 'Suppliers'}
                    </h2>
                    <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                      {importType === 'clients' 
                        ? 'Import your clients from CSV or Excel files. Upload your file to get started.'
                        : importType === 'client-contacts'
                        ? 'Import client contacts from CSV or Excel files. Contacts will be matched to existing clients by company name or email.'
                        : 'Import your suppliers from CSV or Excel files. Upload your file to get started.'}
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Import Type Selection - Only show Clients for now */}
                    <div>
                      <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-3">
                        What would you like to import?
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { value: 'clients', label: 'Clients', icon: Building2, available: true },
                      { value: 'client-contacts', label: 'Client Contacts', icon: User, available: true },
                      { value: 'suppliers', label: 'Suppliers', icon: Building2, available: true },
                    ].map((option) => {
                          const IconComponent = option.icon;
                          return (
                            <label
                              key={option.value}
                              className={`relative flex items-center p-4 border rounded-[var(--ds-radius-md)] transition-all ${
                                !option.available
                                  ? 'opacity-50 cursor-not-allowed'
                                  : importType === option.value
                                  ? 'border-[var(--ds-accent-primary)] bg-[var(--ds-control-field-bg)] cursor-pointer'
                                  : 'border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] hover:border-[var(--ds-border-subtle)] hover:bg-[var(--ds-control-field-bg)] cursor-pointer'
                              }`}
                            >
                              <input
                                type="radio"
                                name="importType"
                                value={option.value}
                                checked={importType === option.value}
                                onChange={(e) => option.available && setImportType(e.target.value)}
                                disabled={!option.available}
                                className="sr-only"
                              />
                              <div className={`flex-shrink-0 w-10 h-10 rounded-[var(--ds-radius-md)] flex items-center justify-center mr-3 ${
                                importType === option.value
                                  ? 'bg-[var(--ds-accent-primary)]/20 text-[var(--ds-accent-primary)]'
                                  : 'bg-[var(--ds-control-field-bg)] text-[var(--ds-text-muted)]'
                              }`}>
                                <IconComponent className="h-5 w-5" />
                              </div>
                              <div className="flex-1">
                                <div className={`text-xs font-medium ${
                                  importType === option.value
                                    ? 'text-[var(--ds-text-primary)]'
                                    : 'text-[var(--ds-text-secondary)]'
                                }`}>
                                  {option.label}
                                </div>
                                {!option.available && (
                                  <div className="text-micro text-[var(--ds-text-muted)] mt-1">
                                    Coming soon
                                  </div>
                                )}
                              </div>
                              {importType === option.value && (
                                <div className="absolute top-2 right-2">
                                  <CheckCircle className="h-5 w-5 text-[var(--ds-accent-primary)]" />
                                </div>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* File Upload Area */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300">
                          Upload File
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadTemplate}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download Template
                        </Button>
                      </div>
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragActive(true);
                        }}
                        onDragLeave={() => setDragActive(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragActive(false);
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            handleImportFileSelect(files[0]);
                          }
                        }}
                        className={`border-2 border-dashed rounded-[var(--ds-radius-md)] p-8 text-center transition-colors ${
                          dragActive
                            ? 'border-[var(--ds-accent-primary)] bg-[var(--ds-accent-primary)]/5'
                            : 'border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel2)]'
                        }`}
                      >
                        {importFile ? (
                          <div className="space-y-4">
                            <div className="flex items-center justify-center">
                              <div className="w-16 h-16 rounded-[var(--ds-radius-md)] bg-[var(--ds-accent-primary)]/20 flex items-center justify-center">
                                <FileSpreadsheet className="h-8 w-8 text-[var(--ds-accent-primary)]" />
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-[var(--ds-text-primary)]">
                                {importFile.name}
                              </p>
                              <p className="text-meta text-[var(--ds-text-muted)] mt-1">
                                {(importFile.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                            <div className="flex items-center justify-center gap-3">
                              <Button
                                variant="outline"
                                size="sm"
                                type="button"
                                onClick={() => {
                                  setImportFile(null);
                                  setImportStep('upload');
                                  setImportMappings(null);
                                  setImportPreviewData(null);
                                  setImportResults(null);
                                }}
                              >
                                <X className="h-4 w-4 mr-2" />
                                Remove File
                              </Button>
                              <label className="inline-flex items-center px-4 py-2 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] text-xs font-medium text-[var(--ds-text-primary)] bg-[var(--ds-surface-panel)] hover:bg-[var(--ds-control-field-bg)] cursor-pointer">
                                <Upload className="h-4 w-4 mr-2" />
                                Change File
                                <input
                                  type="file"
                                  accept=".csv,.xlsx,.xls"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleImportFileSelect(e.target.files[0]);
                                    }
                                  }}
                                  className="hidden"
                                />
                              </label>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex items-center justify-center">
                              <div className="w-16 h-16 rounded-[var(--ds-radius-md)] bg-[var(--ds-control-field-bg)] flex items-center justify-center">
                                <FileSpreadsheet className="h-8 w-8 text-[var(--ds-text-muted)]" />
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-[var(--ds-text-primary)]">
                                Drag & drop your file here
                              </p>
                              <p className="text-meta text-[var(--ds-text-muted)] mt-1">
                                or click to browse
                              </p>
                            </div>
                            <div>
                              <label className="inline-flex items-center px-4 py-2 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] text-xs font-medium text-[var(--ds-text-primary)] bg-[var(--ds-surface-panel)] hover:bg-[var(--ds-control-field-bg)] cursor-pointer">
                                <Upload className="h-4 w-4 mr-2" />
                                Browse Files
                                <input
                                  type="file"
                                  accept=".csv,.xlsx,.xls"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleImportFileSelect(e.target.files[0]);
                                    }
                                  }}
                                  className="hidden"
                                />
                              </label>
                            </div>
                            <p className="text-micro text-[var(--ds-text-muted)] mt-4">
                              Supported formats: CSV, Excel (.xlsx, .xls). Max size: 10MB
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Upload Button */}
                    <div className="pt-4 border-t border-[var(--ds-border-divider)]">
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        disabled={!importFile || importLoading || (importType !== 'clients' && importType !== 'client-contacts' && importType !== 'suppliers')}
                        onClick={handleImportUpload}
                      >
                        {importLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Process File
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Column Mapping */}
            {importStep === 'mapping' && importMappings && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setImportStep('upload')}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                  <div>
                    <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Step 2: Map Columns</h2>
                    <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                      Review and adjust the column mappings detected from your file.
                    </p>
                  </div>
                </div>
                <ImportColumnMapper
                  mappings={importMappings}
                  onMappingsChange={handleMappingsChange}
                  onAutoDetect={handleAutoDetect}
                  importType={importType}
                />
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setImportStep('upload')}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleApplyMappings}
                    disabled={importLoading}
                  >
                    {importLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Continue to Preview'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Preview */}
            {importStep === 'preview' && importPreviewData && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setImportStep('mapping')}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                  <div>
                    <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Step 3: Review & Import</h2>
                    <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                      Review the imported data, fix any errors, and select which rows to import.
                    </p>
                  </div>
                </div>
                <ImportPreview
                  rows={importPreviewData.rows}
                  onRowsChange={handleRowsChange}
                  onImport={handleImportExecute}
                  importType={importType}
                />
              </div>
            )}

            {/* Step 4: Results */}
            {importStep === 'results' && importResults && (
              <Card>
                <CardContent className="p-6">
                  <div className="mb-6">
                    <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Import Complete</h2>
                    <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                      Your import has been processed. Review the results below.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-[var(--ds-radius-md)] bg-[var(--ds-surface-panel2)] border border-[var(--ds-border-subtle)]">
                        <div className="text-micro text-[var(--ds-text-muted)] mb-1">Total Processed</div>
                        <div className="text-h2 font-semibold text-[var(--ds-text-primary)]">{importResults.total}</div>
                      </div>
                      <div className="p-4 rounded-[var(--ds-radius-md)] bg-[var(--ds-status-success-bg)] border border-[var(--ds-status-success)]/30">
                        <div className="text-micro text-[var(--ds-status-success)] mb-1">Imported</div>
                        <div className="text-h2 font-semibold text-[var(--ds-status-success)]">{importResults.imported}</div>
                      </div>
                      <div className="p-4 rounded-[var(--ds-radius-md)] bg-[var(--ds-surface-panel2)] border border-[var(--ds-border-subtle)]">
                        <div className="text-micro text-[var(--ds-text-muted)] mb-1">Skipped</div>
                        <div className="text-h2 font-semibold text-[var(--ds-text-primary)]">{importResults.skipped}</div>
                      </div>
                      <div className="p-4 rounded-[var(--ds-radius-md)] bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30">
                        <div className="text-micro text-[var(--ds-status-danger)] mb-1">Errors</div>
                        <div className="text-h2 font-semibold text-[var(--ds-status-danger)]">{importResults.errors}</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setImportStep('upload');
                          setImportFile(null);
                          setImportMappings(null);
                          setImportPreviewData(null);
                          setImportResults(null);
                        }}
                      >
                        Import Another File
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/tenant/clients')}
                      >
                        View Clients
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Accounting Tab */}
        {!isSettingsHub && activeTab === 'accounting' && (
          <Card>
            <CardContent className="p-6">
              <div className="mb-6">
                <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Chart of Accounts</h2>
                <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                  Manage your accounting Chart of Accounts. Seed the default accounts if they haven't been created yet.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-2">
                        Seed Chart of Accounts
                      </h3>
                      <p className="text-xs text-[var(--ds-text-secondary)] mb-4">
                        Initialize or re-seed the Chart of Accounts for your tenant. This will create all standard accounting accounts (4000-9999) including system accounts. This action is safe to run multiple times - existing accounts will not be duplicated.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSeedChartOfAccounts}
                      disabled={seedingLoading}
                      className="bg-[var(--ds-accent-primary)] text-white hover:opacity-90"
                    >
                      {seedingLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Seeding...
                        </>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 mr-2" />
                          Seed Chart of Accounts
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!isSettingsHub && activeTab === 'backup-export' && (
          <BackupExportSettings toastSuccess={toastSuccess} toastError={toastError} />
        )}

        {/* AI Features Tab */}
        {!isSettingsHub && activeTab === 'ai' && (
          <Card>
            <CardContent className="p-6">
              <div className="mb-6">
                <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">AI Purchase Processing</h2>
                <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                  Configure AI-powered invoice processing. Upload purchase invoices and let AI extract the data automatically.
                </p>
              </div>

              {aiSettingsLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-[var(--ds-text-muted)]" />
                  <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading AI settings...</p>
                </div>
              ) : (
                <form onSubmit={handleAISettingsSubmit} className="space-y-6">
                  {/* Feature Status */}
                  <div className="bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-4">
                    <div className="flex items-center gap-3">
                      {settings.feature_flags?.ai_purchases_enabled ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          <div>
                            <p className="text-xs font-medium text-[var(--ds-text-primary)]">
                              AI Purchase Processing Enabled
                            </p>
                            <p className="text-meta text-[var(--ds-text-secondary)]">
                              This feature has been enabled by your administrator
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                          <div>
                            <p className="text-xs font-medium text-[var(--ds-text-primary)]">
                              AI Purchase Processing Disabled
                            </p>
                            <p className="text-meta text-[var(--ds-text-secondary)]">
                              Contact your administrator to enable this feature
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {settings.feature_flags?.ai_purchases_enabled && (
                    <>
                      {/* Provider Selection */}
                      <div>
                        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                          AI Provider
                        </label>
                        <Select
                          value={aiSettings.provider}
                          onChange={(e) => setAiSettings(prev => ({ ...prev, provider: e.target.value }))}
                          disabled={aiSettingsSaving}
                          className="h-8"
                        >
                          <option value="openai">OpenAI (GPT-5.2)</option>
                          {/* Phase 4: Add other providers */}
                          {/* <option value="claude">Anthropic Claude</option> */}
                          {/* <option value="gemini">Google Gemini</option> */}
                        </Select>
                        <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
                          Select which AI provider to use for invoice processing
                        </p>
                      </div>

                      {/* Model Selection */}
                      <div>
                        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Model
                        </label>
                        <Input
                          type="text"
                          value={aiSettings.model || ''}
                          onChange={(e) => setAiSettings(prev => ({ ...prev, model: e.target.value }))}
                          placeholder="gpt-5.2"
                          disabled={aiSettingsSaving}
                          className="h-8"
                        />
                        <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
                          AI model to use (default: gpt-5.2). Uses OpenAI Responses API for vision capabilities.
                        </p>
                      </div>

                      {/* API Key */}
                      <div>
                        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                          API Key {aiSettings.hasKey && <span className="text-green-500">✓ Configured</span>}
                        </label>
                        <div className="relative">
                          <Input
                            type={showApiKey ? 'text' : 'password'}
                            value={apiKeyInput}
                            onChange={(e) => setApiKeyInput(e.target.value)}
                            placeholder={aiSettings.hasKey ? 'Enter new API key to update' : 'Enter your API key'}
                            disabled={aiSettingsSaving}
                            className="h-8 pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKey(!showApiKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                          >
                            {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
                          {aiSettings.hasKey 
                            ? 'Leave blank to keep existing key, or enter a new key to update'
                            : 'Your API key is encrypted and stored securely. It will never be displayed after saving.'}
                        </p>
                        {aiSettings.provider === 'openai' && (
                          <p className="text-meta text-blue-600 dark:text-blue-400 mt-1">
                            Get your OpenAI API key from{' '}
                            <a 
                              href="https://platform.openai.com/api-keys" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="underline hover:no-underline"
                            >
                              platform.openai.com/api-keys
                            </a>
                          </p>
                        )}
                      </div>

                      {/* Usage Stats */}
                      <div className="bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-4">
                        <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">
                          Usage Statistics
                        </h3>
                        {pricingStatus && !pricingStatus.pricingAvailable && (
                          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-[var(--ds-radius-sm)]">
                            <div className="flex items-start">
                              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                                  Pricing Data Not Available
                                </p>
                                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                  {pricingStatus.message} Cost tracking requires pricing data to be fetched from OpenAI. 
                                  Contact your administrator to update pricing data.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        {usageStatsLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-5 w-5 animate-spin text-[var(--ds-text-muted)]" />
                            <span className="ml-2 text-xs text-[var(--ds-text-secondary)]">Loading statistics...</span>
                          </div>
                        ) : usageStats ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-[var(--ds-surface-elevated)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-sm)] p-3">
                                <div className="text-meta text-[var(--ds-text-secondary)] mb-1">Total API Calls</div>
                                <div className="text-h1 font-semibold text-[var(--ds-text-primary)]">
                                  {usageStats.totalCalls.toLocaleString()}
                                </div>
                              </div>
                              <div className="bg-[var(--ds-surface-elevated)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-sm)] p-3">
                                <div className="text-meta text-[var(--ds-text-secondary)] mb-1">Total Tokens</div>
                                <div className="text-h1 font-semibold text-[var(--ds-text-primary)]">
                                  {usageStats.totalTokens.toLocaleString()}
                                </div>
                              </div>
                              <div className="bg-[var(--ds-surface-elevated)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-sm)] p-3">
                                <div className="text-meta text-[var(--ds-text-secondary)] mb-1">Estimated Cost</div>
                                <div className="text-h1 font-semibold text-[var(--ds-text-primary)]">
                                  ${usageStats.totalCostUsd.toFixed(4)}
                                </div>
                              </div>
                              <div className="bg-[var(--ds-surface-elevated)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-sm)] p-3">
                                <div className="text-meta text-[var(--ds-text-secondary)] mb-1">Errors</div>
                                <div className="text-h1 font-semibold text-[var(--ds-text-primary)]">
                                  {usageStats.errorCount.toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <div className="pt-2 border-t border-[var(--ds-border-divider)]">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-[var(--ds-text-secondary)]">Input Tokens: </span>
                                  <span className="text-[var(--ds-text-primary)] font-medium">
                                    {usageStats.totalInputTokens.toLocaleString()}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-[var(--ds-text-secondary)]">Output Tokens: </span>
                                  <span className="text-[var(--ds-text-primary)] font-medium">
                                    {usageStats.totalOutputTokens.toLocaleString()}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-[var(--ds-text-secondary)]">Avg Processing Time: </span>
                                  <span className="text-[var(--ds-text-primary)] font-medium">
                                    {usageStats.avgProcessingTimeMs > 0 
                                      ? `${(usageStats.avgProcessingTimeMs / 1000).toFixed(2)}s`
                                      : 'N/A'}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-[var(--ds-text-secondary)]">Total Processing Time: </span>
                                  <span className="text-[var(--ds-text-primary)] font-medium">
                                    {usageStats.totalProcessingTimeMs > 0
                                      ? `${(usageStats.totalProcessingTimeMs / 1000).toFixed(1)}s`
                                      : 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-[var(--ds-text-secondary)]">
                            No usage data available yet. Statistics will appear here once you start using AI features.
                          </p>
                        )}
                      </div>

                      {/* Submit Button */}
                      <div className="flex justify-end pt-4 border-t border-[var(--ds-border-divider)]">
                        <Button
                          variant="outline"
                          size="sm"
                          type="submit"
                          disabled={aiSettingsSaving}
                        >
                          {aiSettingsSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            'Save AI Settings'
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payments Tab */}
        {!isSettingsHub && activeTab === 'payments' && (
          <Card>
            <CardContent className="p-6">
              <div className="mb-6">
                <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Payments</h2>
                <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                  Choose Stripe or SumUp so clients can pay invoices online. Store your API keys and webhook secrets securely.
                </p>
              </div>

              {paymentsLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-[var(--ds-text-muted)]" />
                  <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading payments settings...</p>
                </div>
              ) : (
                <form onSubmit={handlePaymentsSubmit} className="space-y-6">
                  {/* Gateway selection */}
                  <div className="space-y-3">
                    <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Payment gateway</h3>
                    <div className="flex flex-wrap gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="paymentGateway"
                          checked={(paymentsData?.activeGateway ?? 'stripe') === 'stripe'}
                          onChange={() => setPaymentsData((prev) => ({ ...(prev || {}), activeGateway: 'stripe' }))}
                          disabled={paymentsSaving}
                          className="h-4 w-4 border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                        />
                        <span className="text-meta font-medium text-gray-700 dark:text-gray-300">Stripe</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="paymentGateway"
                          checked={(paymentsData?.activeGateway ?? 'stripe') === 'sumup'}
                          onChange={() => setPaymentsData((prev) => ({ ...(prev || {}), activeGateway: 'sumup' }))}
                          disabled={paymentsSaving}
                          className="h-4 w-4 border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                        />
                        <span className="text-meta font-medium text-gray-700 dark:text-gray-300">SumUp</span>
                      </label>
                    </div>
                    <p className="text-micro text-[var(--ds-text-muted)]">
                      Only one gateway can be active. Invoice payment links will use the selected gateway.
                    </p>
                  </div>

                  {/* Stripe section — shown when Stripe is selected */}
                  {(paymentsData?.activeGateway ?? 'stripe') === 'stripe' && (
                    <div className="space-y-4">
                      <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Stripe</h3>

                      <div>
                        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Stripe secret key
                          {paymentsData?.stripe?.hasSecretKey && (
                            <span className="ml-2 text-[var(--ds-status-success)]">✓ Saved</span>
                          )}
                        </label>
                        <div className="relative">
                          <Input
                            type={showStripeSecret ? 'text' : 'password'}
                            value={stripeSecretInput}
                            onChange={(e) => {
                              setStripeSecretInput(e.target.value);
                              setStripeSecretDirty(true);
                            }}
                            placeholder={paymentsData?.stripe?.hasSecretKey ? 'Enter new key to update' : 'sk_test_... or sk_live_...'}
                            disabled={paymentsSaving}
                            className="h-8 pr-10 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                          <button
                            type="button"
                            onClick={() => setShowStripeSecret(!showStripeSecret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                            aria-label={showStripeSecret ? 'Hide' : 'Show'}
                          >
                            {showStripeSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                          Create in Stripe Dashboard → Developers → API keys.
                        </p>
                      </div>

                      <div>
                        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Stripe webhook signing secret
                          {paymentsData?.stripe?.hasWebhookSecret && (
                            <span className="ml-2 text-[var(--ds-status-success)]">✓ Saved</span>
                          )}
                        </label>
                        <div className="relative">
                          <Input
                            type={showStripeWebhook ? 'text' : 'password'}
                            value={stripeWebhookInput}
                            onChange={(e) => {
                              setStripeWebhookInput(e.target.value);
                              setStripeWebhookDirty(true);
                            }}
                            placeholder={paymentsData?.stripe?.hasWebhookSecret ? 'Enter new secret to update' : 'whsec_...'}
                            disabled={paymentsSaving}
                            className="h-8 pr-10 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                          <button
                            type="button"
                            onClick={() => setShowStripeWebhook(!showStripeWebhook)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                            aria-label={showStripeWebhook ? 'Hide' : 'Show'}
                          >
                            {showStripeWebhook ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                          From Stripe Dashboard → Developers → Webhooks → [your endpoint] → Signing secret.
                        </p>
                      </div>

                      {(currentWebhookUrl || (paymentsData?.activeGateway ?? 'stripe') === 'stripe') && (
                        <div className="space-y-2">
                          <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Webhook URL to register in Stripe
                          </label>
                          {currentWebhookUrl ? (
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                value={currentWebhookUrl}
                                readOnly
                                className="h-8 flex-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-xs"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleCopyWebhookUrl}
                                className="shrink-0"
                              >
                                <Copy className="h-4 w-4 mr-1" />
                                Copy
                              </Button>
                            </div>
                          ) : (
                            <p className="text-micro text-[var(--ds-text-muted)]">Loading webhook URL…</p>
                          )}
                          <div className="rounded-lg border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-subtle)] p-3 text-micro text-[var(--ds-text-secondary)]">
                            <p className="font-medium text-[var(--ds-text-primary)] mb-2">Setup instructions</p>
                            <ol className="list-decimal list-inside space-y-1">
                              <li>Copy the webhook URL above and go to <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-[var(--ds-accent-primary)] underline">Stripe Dashboard → Developers → Webhooks</a> for the <span className="font-medium">same Stripe account and mode</span> (test or live) as your secret key</li>
                              <li>Click &quot;Add endpoint&quot; and paste the URL (this must be your <span className="font-medium">public API</span> base, not a frontend dev server URL)</li>
                              <li>Select events: <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">checkout.session.completed</code>, <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">payment_intent.succeeded</code>, <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">payment_intent.payment_failed</code></li>
                              <li>After creating, reveal the signing secret (starts with <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">whsec_</code>) — it must be from <span className="font-medium">this</span> endpoint only</li>
                              <li>Paste it in the &quot;Stripe webhook signing secret&quot; field above and save</li>
                              <li>In Stripe, open the endpoint → recent deliveries and confirm SparxMate responds with <span className="font-medium">2xx</span> after payments (fix mismatched secrets or URL if you see 4xx/5xx)</li>
                            </ol>
                            <p className="mt-2 text-[var(--ds-text-muted)]">Without a valid webhook, invoices will not update when clients pay and you will not receive payment notifications.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SumUp section — shown when SumUp is selected */}
                  {(paymentsData?.activeGateway ?? 'stripe') === 'sumup' && (
                    <div className="space-y-4">
                      <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">SumUp</h3>

                      <div>
                        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                          SumUp API key
                          {paymentsData?.sumup?.hasApiKey && (
                            <span className="ml-2 text-[var(--ds-status-success)]">✓ Saved</span>
                          )}
                        </label>
                        <div className="relative">
                          <Input
                            type={showSumupApiKey ? 'text' : 'password'}
                            value={sumupApiKeyInput}
                            onChange={(e) => {
                              setSumupApiKeyInput(e.target.value);
                              setSumupApiKeyDirty(true);
                            }}
                            placeholder={paymentsData?.sumup?.hasApiKey ? 'Enter new key to update' : 'sup_sk_... or sup_test_...'}
                            disabled={paymentsSaving}
                            className="h-8 pr-10 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSumupApiKey(!showSumupApiKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                            aria-label={showSumupApiKey ? 'Hide' : 'Show'}
                          >
                            {showSumupApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                          Create in SumUp Developer Dashboard. Use <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">sup_sk_</code> (production) or <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">sup_test_</code> (sandbox).
                        </p>
                        <p className="mt-1 text-micro">
                          <a href="https://developer.sumup.com" target="_blank" rel="noopener noreferrer" className="text-[var(--ds-accent-primary)] underline hover:no-underline">SumUp Developer Dashboard</a>
                        </p>
                      </div>

                      <div>
                        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                          SumUp webhook secret <span className="text-[var(--ds-text-muted)] font-normal">(optional)</span>
                          {paymentsData?.sumup?.hasWebhookSecret && (
                            <span className="ml-2 text-[var(--ds-status-success)]">✓ Saved</span>
                          )}
                        </label>
                        <div className="relative">
                          <Input
                            type={showSumupWebhook ? 'text' : 'password'}
                            value={sumupWebhookInput}
                            onChange={(e) => {
                              setSumupWebhookInput(e.target.value);
                              setSumupWebhookDirty(true);
                            }}
                            placeholder={paymentsData?.sumup?.hasWebhookSecret ? 'Enter new secret to update' : 'Webhook signing secret'}
                            disabled={paymentsSaving}
                            className="h-8 pr-10 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSumupWebhook(!showSumupWebhook)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                            aria-label={showSumupWebhook ? 'Hide' : 'Show'}
                          >
                            {showSumupWebhook ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                          From SumUp Developer Dashboard → Webhooks → [your endpoint] → Signing secret (if you use signed webhooks).
                        </p>
                      </div>

                      {currentWebhookUrl && (
                        <div>
                          <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Webhook URL to register in SumUp
                          </label>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              value={currentWebhookUrl}
                              readOnly
                              className="h-8 flex-1 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-xs"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleCopyWebhookUrl}
                              className="shrink-0"
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Copy
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Setup instructions (collapsible) */}
                  <div className="border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] overflow-hidden bg-[var(--ds-surface-panel2)]">
                    <button
                      type="button"
                      onClick={() => setPaymentsShowSetup(!paymentsShowSetup)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)] transition-colors"
                    >
                      <span className="text-meta font-medium">Setup instructions</span>
                      {paymentsShowSetup ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {paymentsShowSetup && (
                      <div className="px-4 pb-4 pt-0 space-y-4 text-micro text-[var(--ds-text-secondary)] border-t border-[var(--ds-border-subtle)]">
                        {(paymentsData?.activeGateway ?? 'stripe') === 'stripe' && (
                          <>
                            <div>
                              <p className="font-medium text-[var(--ds-text-primary)] mb-1">Getting API keys</p>
                              <ol className="list-decimal list-inside space-y-1">
                                <li>Go to <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-[var(--ds-accent-primary)] underline hover:no-underline">Stripe Dashboard</a> → Developers → API keys.</li>
                                <li>Use &quot;Test&quot; keys for testing, &quot;Live&quot; for production.</li>
                                <li>Copy the &quot;Secret key&quot; (starts with <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">sk_test_</code> or <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">sk_live_</code>) into the Secret key field. Never share or commit it.</li>
                              </ol>
                              <p className="mt-2">
                                <a href="https://docs.stripe.com/keys" target="_blank" rel="noopener noreferrer" className="text-[var(--ds-accent-primary)] underline hover:no-underline">Stripe: API keys</a>
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-[var(--ds-text-primary)] mb-1">Setting up the webhook</p>
                              <ol className="list-decimal list-inside space-y-1">
                                <li>Developers → Webhooks → &quot;Add endpoint&quot;.</li>
                                <li>Endpoint URL: paste the &quot;Webhook URL&quot; shown above (unique per tenant).</li>
                                <li>Select events: <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">checkout.session.completed</code>, <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">payment_intent.succeeded</code>, <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">payment_intent.payment_failed</code>.</li>
                                <li>Create endpoint, then copy the &quot;Signing secret&quot; (starts with <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">whsec_</code>) into the Webhook signing secret field.</li>
                              </ol>
                              <p className="mt-2">
                                <a href="https://docs.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-[var(--ds-accent-primary)] underline hover:no-underline">Stripe: Webhooks</a>
                              </p>
                            </div>
                          </>
                        )}
                        {(paymentsData?.activeGateway ?? 'stripe') === 'sumup' && (
                          <>
                            <div>
                              <p className="font-medium text-[var(--ds-text-primary)] mb-1">Getting SumUp API key</p>
                              <ol className="list-decimal list-inside space-y-1">
                                <li>Go to <a href="https://developer.sumup.com" target="_blank" rel="noopener noreferrer" className="text-[var(--ds-accent-primary)] underline hover:no-underline">SumUp Developer Dashboard</a>.</li>
                                <li>Create or sign in to your SumUp developer account.</li>
                                <li>Create an API key (starts with <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">sup_sk_</code> for production or <code className="bg-[var(--ds-control-field-bg)] px-1 rounded">sup_test_</code> for sandbox). Never share or commit it.</li>
                              </ol>
                              <p className="mt-2">
                                <a href="https://developer.sumup.com/docs" target="_blank" rel="noopener noreferrer" className="text-[var(--ds-accent-primary)] underline hover:no-underline">SumUp: Developer docs</a>
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-[var(--ds-text-primary)] mb-1">Setting up the webhook</p>
                              <ol className="list-decimal list-inside space-y-1">
                                <li>In SumUp Developer Dashboard, configure your webhook endpoint.</li>
                                <li>Endpoint URL: paste the &quot;Webhook URL&quot; shown above (unique per tenant).</li>
                                <li>If SumUp provides a signing secret, enter it in the Webhook secret field above.</li>
                              </ol>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Enable Pay in portal toggle */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="pay-enabled"
                      checked={paymentsData?.payEnabled ?? true}
                      onChange={(e) => setPaymentsData((prev) => ({ ...(prev || {}), payEnabled: e.target.checked }))}
                      disabled={paymentsSaving}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                    />
                    <label htmlFor="pay-enabled" className="text-meta font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                      Allow clients to pay invoices online
                    </label>
                  </div>
                  <p className="text-micro text-[var(--ds-text-muted)] -mt-2">
                    When disabled, &quot;Pay now&quot; (banner) and &quot;Pay&quot; (client Invoices) are hidden.
                  </p>

                  <div className="pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      type="submit"
                      disabled={paymentsSaving}
                    >
                      {paymentsSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* Integrations Tab */}
        {!isSettingsHub && activeTab === 'integrations' && (
          <Card>
            <CardContent className="p-6">
              <div className="mb-6">
                <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Integrations</h2>
                <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                  Connect external services. Configure OAuth credentials for Google Calendar to enable calendar sync in the Planner.
                </p>
              </div>

              {!isTenantAdmin() ? (
                <p className="text-meta text-[var(--ds-text-muted)]">
                  Only tenant administrators can configure integrations.
                </p>
              ) : googleCalendarLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-[var(--ds-text-muted)]" />
                  <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading integrations...</p>
                </div>
              ) : (
                <form onSubmit={handleGoogleCalendarSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Google Calendar</h3>
                    <p className="text-micro text-[var(--ds-text-muted)]">
                      Add your Google OAuth Client ID and Client Secret so users can connect their Google Calendar and view events in the Planner.
                    </p>

                    <div>
                      <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Google OAuth Client ID
                        {googleCalendarData?.hasClientId && (
                          <span className="ml-2 text-[var(--ds-status-success)]">✓ Saved</span>
                        )}
                      </label>
                      <div className="relative">
                        <Input
                          type={showGoogleClientId ? 'text' : 'password'}
                          value={googleClientIdInput}
                          onChange={(e) => {
                            setGoogleClientIdInput(e.target.value);
                            setGoogleClientIdDirty(true);
                          }}
                          placeholder={googleCalendarData?.hasClientId ? 'Enter new Client ID to update' : 'xxxxx.apps.googleusercontent.com'}
                          disabled={googleCalendarSaving}
                          className="h-8 pr-10 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        <button
                          type="button"
                          onClick={() => setShowGoogleClientId(!showGoogleClientId)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                          aria-label={showGoogleClientId ? 'Hide' : 'Show'}
                        >
                          {showGoogleClientId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Google OAuth Client Secret
                        {googleCalendarData?.hasClientSecret && (
                          <span className="ml-2 text-[var(--ds-status-success)]">✓ Saved</span>
                        )}
                      </label>
                      <div className="relative">
                        <Input
                          type={showGoogleClientSecret ? 'text' : 'password'}
                          value={googleClientSecretInput}
                          onChange={(e) => {
                            setGoogleClientSecretInput(e.target.value);
                            setGoogleClientSecretDirty(true);
                          }}
                          placeholder={googleCalendarData?.hasClientSecret ? 'Enter new secret to update' : 'GOCSPX-...'}
                          disabled={googleCalendarSaving}
                          className="h-8 pr-10 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        <button
                          type="button"
                          onClick={() => setShowGoogleClientSecret(!showGoogleClientSecret)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                          aria-label={showGoogleClientSecret ? 'Hide' : 'Show'}
                        >
                          {showGoogleClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-4 bg-[var(--ds-surface-panel2)] space-y-2">
                      <p className="text-micro font-medium text-[var(--ds-text-primary)]">Setup instructions</p>
                      <ol className="list-decimal list-inside space-y-1 text-micro text-[var(--ds-text-secondary)]">
                        <li>
                          Go to{' '}
                          <a
                            href="https://console.cloud.google.com/apis/credentials"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--ds-accent-primary)] underline hover:no-underline"
                          >
                            Google Cloud Console → APIs &amp; Services → Credentials
                          </a>
                        </li>
                        <li>Create OAuth 2.0 Client ID (Web application). Add redirect URI: <code className="bg-[var(--ds-control-field-bg)] px-1 rounded text-xs">{typeof window !== 'undefined' ? `${window.location.origin}/api/integrations/google/callback` : '/api/integrations/google/callback'}</code></li>
                        <li>Required scope: <code className="bg-[var(--ds-control-field-bg)] px-1 rounded text-xs">https://www.googleapis.com/auth/calendar.readonly</code></li>
                      </ol>
                    </div>

                    <div className="pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        type="submit"
                        disabled={googleCalendarSaving || (!googleClientIdDirty && !googleClientSecretDirty)}
                      >
                        {googleCalendarSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* HMRC MTD Tab */}
        {!isSettingsHub && activeTab === 'hmrc' && (
          <HmrcSettings key={hmrcRefreshKey} />
        )}

        {/* Security / Passkeys Tab */}
        {!isSettingsHub && activeTab === 'security' && supportsPasskey && (
          <Card>
            <CardContent className="p-6">
              <div className="mb-6">
                <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Passkeys</h2>
                <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
                  Add passkeys to sign in with biometrics or a security key instead of your password.
                </p>
              </div>

              {passkeyLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-[var(--ds-text-muted)]" />
                  <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading passkeys...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddPasskey}
                      disabled={passkeyAdding}
                    >
                      {passkeyAdding ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Fingerprint className="h-4 w-4 mr-2" />
                          Add passkey
                        </>
                      )}
                    </Button>
                  </div>

                  {passkeyCredentials.length > 0 && (
                    <div>
                      <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-3">Your passkeys</h3>
                      <ul className="space-y-2">
                        {passkeyCredentials.map((cred) => (
                          <li
                            key={cred.id}
                            className="flex items-center justify-between py-3 px-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel2)]"
                          >
                            <div className="flex items-center gap-3">
                              <Fingerprint className="h-5 w-5 text-[var(--ds-text-muted)]" />
                              <div>
                                <p className="text-body font-medium text-[var(--ds-text-primary)]">
                                  {cred.deviceType === 'multiDevice'
                                    ? 'Synced passkey'
                                    : cred.deviceType === 'singleDevice'
                                    ? 'Device passkey'
                                    : 'Passkey'}
                                </p>
                                <p className="text-micro text-[var(--ds-text-muted)]">
                                  Added {cred.createdAt ? new Date(cred.createdAt).toLocaleDateString() : '—'}
                                  {cred.lastUsedAt && ` · Last used ${new Date(cred.lastUsedAt).toLocaleDateString()}`}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)] hover:bg-[var(--ds-status-danger)]/10"
                              onClick={() => handleDeletePasskey(cred.id)}
                              disabled={passkeyDeletingId === cred.id}
                              aria-label="Remove passkey"
                            >
                              {passkeyDeletingId === cred.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {passkeyCredentials.length === 0 && !passkeyLoading && (
                    <p className="text-micro text-[var(--ds-text-muted)]">
                      No passkeys yet. Add one to sign in quickly with fingerprint, Face ID, or a security key.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      <ToastContainer />
    </Layout>
  );
}


